import { createRouter, createWebHistory, RouterView } from 'vue-router';
import { h } from 'vue';
import { i18n } from '@/i18n';
import { SUPPORTED_LOCALES } from '@/composables/useLocale';
import { malukuRoutes } from './malukuRoutes';
import HomePage from '@/views/HomePage.vue';
import PersonPage from '@/views/people/PersonPage.vue';
import AncestorTreeUnified from '@/views/trees/AncestorTreeUnified.vue';
import DescendantTree from '@/views/trees/DescendantTree.vue';
import RelationshipTree from '@/views/trees/RelationshipTree.vue';
import BlogOverview from '@/views/blog/BlogOverview.vue';
import BlogPost from '@/views/blog/BlogPost.vue';
import LoginPage from '@/views/auth/LoginPage.vue';
import SearchResults from '@/views/search/SearchResults.vue';
import RecentChanges from '@/views/overviews/RecentChanges.vue';
import NotFound from '@/views/special/NotFound.vue';
import AboutPage from '@/views/info/AboutPage.vue';
import ContactPage from '@/views/info/ContactPage.vue';
import ResearchPage from '@/views/info/ResearchPage.vue';
import MediaOverview from '@/views/overviews/MediaOverview.vue';
import Stamboom from '@/views/trees/Stamboom.vue';
import PlaceOverview from '@/views/overviews/PlaceOverview.vue';
import GeneticMatches from '@/views/overviews/GeneticMatches.vue';
import EventCalendar from '@/views/overviews/EventCalendar.vue';
import SiteStatsOverview from '@/views/overviews/SiteStatsOverview.vue';
import SiteStatsGrowth from '@/views/overviews/SiteStatsGrowth.vue';
import ServerStatsStandalone from '@/views/overviews/ServerStatsStandalone.vue';

import FamilyTree from '@/views/trees/FamilyTree.vue';
// import FamilyNameEvolution from '@/components/experiments/FamilyNameEvolution.vue';
// import PlacesEvolution from '@/components/experiments/PlacesEvolution.vue';
// import FamilyNameRace from '@/components/experiments/FamilyNameRace.vue';
// import SunburstChart from '@/components/experiments/SunburstChart.vue';
// import AncestorTreeTest from '@/components/experiments/AncestorTreeTest.vue';
// import RadialFamilyTree from '@/components/experiments/RadialFamilyTree.vue';
// import PedigreeFanChart from '@/components/experiments/PedigreeFanChart.vue';
// import ChronologicalChart from '@/components/experiments/ChronologicalChart.vue';
// import WedgeChart from '@/components/experiments/WedgeChart.vue';
// import InteractiveTreeMap from '@/components/experiments/InteractiveTreeMap.vue';
// import fancharttest from '@/components/experiments/fancharttest.vue';

import PlaceEvents from '../views/overviews/PlaceEvents.vue';

// Experimental routes are lazy-loaded to keep the main bundle small for normal users.
const experimentRoutes = [
  {
    path: 'experimenten/family-name-evolution',
    name: 'ExperimentFamilyNameEvolution',
    component: () => import('@/components/experiments/FamilyNameEvolution.vue')
  },
  {
    path: 'experimenten/family-name-bump-chart',
    name: 'ExperimentFamilyNameBumpChart',
    component: () => import('@/components/experiments/FamilyNameBumpChart.vue')
  },
  {
    path: 'experimenten/places-evolution',
    name: 'ExperimentPlacesEvolution',
    component: () => import('@/components/experiments/PlacesEvolution.vue')
  },
  {
    path: 'experimenten/family-name-race',
    name: 'ExperimentFamilyNameRace',
    component: () => import('@/components/experiments/FamilyNameRace.vue')
  },
  {
    path: 'experimenten/sunburst-chart',
    name: 'ExperimentSunburstChart',
    component: () => import('@/components/experiments/SunburstChart.vue')
  },
  {
    path: 'experimenten/ancestor-tree-test',
    name: 'ExperimentAncestorTreeTest',
    component: () => import('@/components/experiments/AncestorTreeTest.vue')
  },
  {
    path: 'experimenten/radial-family-tree',
    name: 'ExperimentRadialFamilyTree',
    component: () => import('@/components/experiments/RadialFamilyTree.vue')
  },
  {
    path: 'experimenten/pedigree-fan-chart',
    name: 'ExperimentPedigreeFanChart',
    component: () => import('@/components/experiments/PedigreeFanChart.vue')
  },
  {
    path: 'experimenten/chronological-chart',
    name: 'ExperimentChronologicalChart',
    component: () => import('@/components/experiments/ChronologicalChart.vue')
  },
  {
    path: 'experimenten/interactive-tree-map',
    name: 'ExperimentInteractiveTreeMap',
    component: () => import('@/components/experiments/InteractiveTreeMap.vue')
  },
  {
    path: 'experimenten/fancharttest',
    name: 'ExperimentFancharttest',
    component: () => import('@/components/experiments/fancharttest.vue')
  }
];

experimentRoutes.forEach(route => {
  route.meta = { ...(route.meta || {}), noindex: true };
});

// All content routes are declared without a leading slash so they can be mounted
// under the optional locale parent route (/:locale?).
const relationStatsRedirect = (to) => {
  const locale = typeof to.params.locale === 'string' ? to.params.locale : '';
  return {
    path: locale ? `/${locale}/stamboom` : '/stamboom',
    query: { view: 'relaties' },
    replace: true,
  };
};

const legacyMediaRedirect = (type) => (to) => {
  const localeParam = typeof to.params.locale === 'string' ? to.params.locale : '';
  const locale = localeParam ? `/${localeParam}` : '';
  return {
    path: `${locale}/fotos-en-documenten`,
    query: { ...to.query, type },
    hash: to.hash,
    replace: true,
  };
};

const contentRoutes = [
  malukuRoutes,
  {
    // Handles /nl, /en, /id (locale-prefixed home pages)
    path: '',
    name: 'HomeLocale',
    component: HomePage
  },
  {
    path: 'nieuw',
    name: 'RecentChanges',
    component: RecentChanges,
  },
  {
    path: 'gezocht',
    name: 'ResearchPage',
    component: ResearchPage,
  },
  {
    path: 'over',
    name: 'AboutPage',
    component: AboutPage,
  },
  {
    path: 'contact',
    name: 'ContactPage',
    component: ContactPage,
  },
  {
    path: 'person/:id',
    name: 'PersonPage',
    component: PersonPage,
  },
  {
    path: 'person/:personId/ancestors',
    name: 'AncestorTree',
    component: AncestorTreeUnified,
    props: true,
    meta: { hideNavAndFooter: true, noindex: true }
  },
  {
    path: 'person/:personId/ancestors/radial',
    name: 'RadialAncestorTree',
    redirect: to => ({
      path: `/person/${to.params.personId}/ancestors`,
      query: { view: 'radial' }
    })
  },
  {
    path: 'person/:personId/descendants',
    name: 'DescendantTree',
    component: DescendantTree,
    props: true,
    meta: { hideNavAndFooter: true, noindex: true }
  },
  {
    path: 'relationship/:personID1?/:personID2?',
    name: 'RelationshipTree',
    component: RelationshipTree,
    props: true,
    meta: { hideNavAndFooter: true, noindex: true }
  },
  {
    path: 'documenten',
    name: 'LegacyDocumentOverview',
    redirect: legacyMediaRedirect('documents'),
  },
  {
    path: 'fotos-en-documenten',
    name: 'MediaOverview',
    component: MediaOverview,
  },
  {
    path: 'achternamen',
    name: 'NameOverview',
    redirect: to => ({ name: 'Stamboom', params: { locale: to.params.locale }, query: { view: 'achternamen' } }),
  },
  {
    path: 'achternamen-personen',
    name: 'NameOverviewPeople',
    redirect: to => ({ name: 'Stamboom', params: { locale: to.params.locale } }),
  },
  {
    path: 'stamboom',
    name: 'Stamboom',
    component: Stamboom,
    meta: { hideNavAndFooter: true },
  },
  {
    path: 'fotos',
    name: 'LegacyPhotoOverview',
    redirect: legacyMediaRedirect('photos'),
  },
  {
    path: 'fam',
    name: 'FamilyTree',
    component: FamilyTree,
    meta: { noindex: true },
  },
  {
    path: 'plaatsen',
    name: 'PlaceOverview',
    component: PlaceOverview,
    meta: { hideNavAndFooter: true, noindex: true },
  },
  {
    path: 'plaatsen/:id',
    name: 'PlaceEvents',
    component: PlaceEvents,
    meta: { noindex: true },
  },
  {
    path: 'dna',
    name: 'GeneticMatches',
    component: GeneticMatches,
  },
  {
    path: 'dna-matches',
    redirect: to => ({
      name: 'GeneticMatches',
      params: { locale: to.params.locale },
      query: to.query,
      hash: to.hash,
      replace: true,
    }),
  },
  {
    path: 'kalender',
    name: 'EventCalendar',
    component: EventCalendar,
  },
  {
    path: 'statistieken',
    name: 'SiteStatsOverview',
    component: SiteStatsOverview,
    beforeEnter: (to) => {
      if (to.query.category === 'relaties') {
        return relationStatsRedirect(to);
      }
      return true;
    },
  },
  {
    path: 'statistieken/groei',
    name: 'SiteStatsGrowth',
    component: SiteStatsGrowth,
  },
  {
    path: 'statistieken/relaties',
    name: 'SiteStatsRelations',
    redirect: relationStatsRedirect,
  },
  {
    path: 'server-stats',
    name: 'ServerStatsStandalone',
    component: ServerStatsStandalone,
    meta: { hideNavAndFooter: true, requiresAuth: true, noindex: true },
  },
  {
    path: 'beheer/activiteit',
    name: 'SecurityActivityDashboard',
    component: () => import('@/views/admin/SecurityActivityDashboard.vue'),
    meta: { hideNavAndFooter: true, requiresAuth: true, noindex: true },
  },
  {
    path: 'verhalen',
    name: 'BlogOverview',
    component: BlogOverview
  },
  {
    path: 'verhalen/:slug',
    name: 'BlogPost',
    component: BlogPost
  },
  {
    path: 'login',
    name: 'Login',
    component: LoginPage,
    meta: { noindex: true },
  },
  {
    path: 'search',
    name: 'SearchResults',
    component: SearchResults,
    meta: { noindex: true },
  },
  {
    path: 'experimenten',
    name: 'ExperimentIndex',
    component: () => import('@/views/experiments/ExperimentIndex.vue'),
    meta: { noindex: true },
  },
  {
    path: 'dashboard-to-calendar',
    name: 'DashboardToCalendar',
    meta: { noindex: true },
    beforeEnter() {
      window.location.href = '/dashboard_to_calendar.html';
      return false;
    }
  },
  ...experimentRoutes,
  // Catch unmatched paths within a locale prefix (e.g. /en/nonexistent)
  {
    path: ':catchAll(.*)',
    component: NotFound,
    meta: { hideNavAndFooter: true, noindex: true }
  },
];

const routes = [
  // Explicit home route — fixes Vue Router 4 edge case where optional param parent
  // (/:locale?) + empty child path ('') doesn't reliably match the bare '/' URL.
  {
    path: '/',
    name: 'Home',
    component: HomePage
  },
  // Explicit locale home route — ensures /nl, /en and /id never fall through
  // to catch-all handlers while still populating to.params.locale.
  {
    path: `/:locale(${SUPPORTED_LOCALES.join('|')})`,
    name: 'HomeByLocale',
    component: HomePage,
  },
  {
    // Optional locale prefix: /nl/..., /en/..., /id/... or no prefix (defaults to Dutch)
    // This parent uses RouterView so children can share the same locale-aware shell.
    path: `/:locale(${SUPPORTED_LOCALES.join('|')})?`,
    component: { render: () => h(RouterView) },
    children: contentRoutes,
  },
  // {
  //   path: '/tng-admin/readme',
  //   name: 'Readme',
  //   // No need for beforeEnter, just navigate to the static URL
  //   component: {
  //     template: '<div>Redirecting...</div>',
  //     mounted() {
  //       window.location.href = '/tng-admin/readme.html';
  //     }
  //   }
  // }

  // {
  //   path: '/experimenten/hs-dashboard',
  //   name: 'Login',
  //   component: LoginPage
  // },


  // {
  //   path: '/fne',
  //   name: 'FamilyNameEvolution',
  //   component: FamilyNameEvolution,
  // }, {
  //   path: '/p',
  //   name: 'PlacesEvolution',
  //   component: PlacesEvolution,
  // }, {
  //   path: '/t',
  //   name: 'FamilyNameRace',
  //   component: FamilyNameRace,
  // }, {
  //   path: '/sunburstchart',
  //   name: 'SunburstChart',
  //   component: SunburstChart,
  // }, {
  //   path: '/ancestortreetest',
  //   name: 'AncestorTreeTest',
  //   component: AncestorTreeTest,
  // },
  // {
  //   path: '/radialfamilytree',
  //   name: 'RadialFamilyTree',
  //   component: RadialFamilyTree,
  // },
  // {
  //   path: '/PedigreeFanChart',
  //   name: 'PedigreeFanChart',
  //   component: PedigreeFanChart,
  // },
  // {
  //   path: '/ChronologicalChart',
  //   name: 'ChronologicalChart',
  //   component: ChronologicalChart,
  // },
  // {
  //   path: '/fancharttest',
  //   name: 'fancharttest',
  //   component: fancharttest,
  // },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  // scrollBehavior(to, from, savedPosition) {
  //   // If the route has the preserveScroll meta and query parameters are changed, don't reset scroll
  //   if (to.meta.preserveScroll && to.fullPath !== from.fullPath) {
  //     return {};
  //   }

  //   // For all other routes or if the fullPath hasn't changed, reset scroll to top
  //   if (savedPosition) {
  //     return savedPosition;
  //   } else {
  //     return { top: 0 };
  //   }
  // }
});

// First guard keeps locale state in sync with URL and enforces auth-protected pages.
router.beforeEach((to) => {
  // Normalize trailing slash paths (except root) so locale home URLs like
  // /nl/, /en/, /id/ resolve to /nl, /en, /id and match HomeLocale.
  if (to.path.length > 1 && /\/+$/g.test(to.path)) {
    return {
      path: to.path.replace(/\/+$/g, ''),
      query: to.query,
      hash: to.hash,
      replace: true,
    };
  }

  // Dutch remains the implicit default when no locale prefix is present.
  const locale = to.params.locale || 'nl';
  i18n.global.locale.value = locale;
  localStorage.setItem('locale', locale);

  if (to.matched.some(record => record.meta.requiresAuth)) {
    const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!authToken) {
      const localizedLoginPath = typeof to.params.locale === 'string' ? `/${to.params.locale}/login` : '/login';
      return {
        path: localizedLoginPath,
        query: { redirect: to.fullPath },
        replace: true,
      };
    }
  }
});

// Second guard updates canonical and social URL tags so crawlers always index a
// normalized route without query/hash or trailing slash noise.
router.beforeEach((to, from, next) => {
  // Build a normalized canonical URL (no query/hash, no trailing slash except root)
  const baseUrl = 'https://sejarah-thumann.online';
  const normalizedPath = to.path !== '/' ? to.path.replace(/\/+$/, '') : '/';
  const canonicalUrl = `${baseUrl}${normalizedPath}`;

  // Set canonical link
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = canonicalUrl;

  // Update og:url meta tag
  let ogUrlMeta = document.querySelector('meta[property="og:url"]');
  if (!ogUrlMeta) {
    ogUrlMeta = document.createElement('meta');
    ogUrlMeta.setAttribute('property', 'og:url');
    document.head.appendChild(ogUrlMeta);
  }
  ogUrlMeta.setAttribute('content', canonicalUrl);

  next();
});

router.afterEach((to) => {
  const locale = to.params.locale || 'nl';
  document.documentElement.lang = locale;

  document.querySelectorAll('meta[name="robots"]').forEach(element => element.remove());
  const robots = document.createElement('meta');
  robots.name = 'robots';
  robots.content = to.matched.some(record => record.meta.noindex) ? 'noindex,follow' : 'index,follow';
  document.head.appendChild(robots);
});

export default router;
